import { useCallback, useState } from "react";
import { v4 as uuid } from "uuid";
import useMediaCreate from "../api/useMediaCreate";
import useMediaUpdateStatus from "../api/useMediaUpdateStatus";
import useMediaUpload from "../api/useMediaUpload";
import { BlobContainer, MediaUploadItem } from "../types";

export default function useFileUploader() {
    const [uploadItems, setUploadItems] = useState<MediaUploadItem[]>([]);
    const { mediaCreate, error: mediaCreateError } = useMediaCreate();
    const { mediaUpdateStatus, error: mediaUpdateStatusError } = useMediaUpdateStatus();
    const { mediaUpload } = useMediaUpload();

    const reset = () => setUploadItems([]);

    const uploadFiles = useCallback(
        (files: BlobContainer[]) => {
            const create = (item: MediaUploadItem) => {
                setUploadItems(current => [item, ...current]);
            };

            const update = (item: MediaUploadItem) => {
                setUploadItems(current =>
                    current.map(currentItem => {
                        if (currentItem.id === item.id) {
                            return item;
                        } else {
                            return currentItem;
                        }
                    }),
                );
            };

            Promise.all(
                files.map(async file => {
                    const id = uuid();
                    create({
                        id,
                        type: "CreatingMedia",
                        fileName: file.name,
                    });

                    const {
                        name,
                        blob: { type },
                    } = file;

                    const { data: createdMedia } = await mediaCreate(name, type);
                    if (!createdMedia) return;
                    const { mediaCreateUploadable: media } = createdMedia;
                    if (!media.uploadUrl) {
                        update({
                            id,
                            type: "ErrorMedia",
                            fileName: file.name,
                            error: "Could not create upload url",
                        });
                        return;
                    }
                    const uploadItem: MediaUploadItem = {
                        id,
                        type: "UploadingMedia",
                        fileName: media.name,
                        mediaItem: media,
                        progress: 0,
                    };
                    update(uploadItem);

                    await mediaUpload(file, media.uploadUrl, progress => {
                        update({ ...uploadItem, progress });
                    });

                    const { data: updatedMedia } = await mediaUpdateStatus(media.id);
                    if (!updatedMedia) return;
                    update({
                        id,
                        type: "FinishedMedia",
                        fileName: file.name,
                        mediaItem: updatedMedia.mediaSetUploaded,
                    });
                }),
            );
        },
        [mediaCreate, mediaUpload, mediaUpdateStatus],
    );

    return {
        uploadItems,
        uploadFiles,
        error: mediaCreateError || mediaUpdateStatusError,
        reset,
    };
}
